import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCurrentSchool, ASSETS } from '../config/schools';
import { getUser, logout, isSuperAdmin, isEducatorDemo } from '../utils/auth';
import { UniverseToggle } from './UniverseSelector';
import { FaSignOutAlt, FaEnvelope, FaNewspaper, FaChartLine } from 'react-icons/fa';
import NotificationCenter from './NotificationCenter';

function AppHeader({ onTriggerSplash }) {
  const navigate = useNavigate();
  const school = getCurrentSchool();
  const user = getUser();
  const [clickCount, setClickCount] = useState(0);
  const clickTimerRef = useRef(null);
  const showAdminLinks = isSuperAdmin() || isEducatorDemo();

  // Easter egg: 3 clics sur le logo = replay splash
  const handleLogoClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);

    // Reset timer
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    // Reset click count after 1 second of inactivity
    clickTimerRef.current = setTimeout(() => {
      setClickCount(0);
    }, 1000);

    // Trigger splash on 3 clicks
    if (newCount >= 3) {
      setClickCount(0);
      if (onTriggerSplash) {
        onTriggerSplash();
      } else if (window.triggerSplash) {
        // Use global function from App.js
        window.triggerSplash();
      } else {
        // Fallback: clear session storage and reload
        sessionStorage.removeItem('hasSeenSplash');
        window.location.href = '/';
      }
    }
  };

  return (
    <header className="border-b-4 border-black" style={{ background: '#1B4965' }}>
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        {/* Logo avec easter egg */}
        <motion.button 
          onClick={handleLogoClick}
          className="flex items-center space-x-4 hover:opacity-80 transition-opacity"
          whileTap={{ scale: 0.95 }}
          title="Triple-clic pour revoir l'animation"
          data-testid="logo-easter-egg"
        >
          {school.showSchoolLogo && school.logo ? (
            <img 
              src={school.logo}
              alt={school.name}
              className="w-10 h-10 object-contain"
            />
          ) : (
            <img 
              src={ASSETS.DAILYGLOBE_LOGO}
              alt="DailyGlobe"
              className="w-10 h-10 object-contain"
            />
          )}
          <span className="text-xl font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
            {school.showSchoolLogo ? school.name : 'DAILYGLOBE SCHOOL'}
          </span>
          
          {/* Visual feedback for easter egg progress */}
          {clickCount > 0 && (
            <div className="flex space-x-1 ml-2">
              {[1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0 }}
                  animate={{ scale: i <= clickCount ? 1 : 0.5, opacity: i <= clickCount ? 1 : 0.3 }}
                  className="w-2 h-2 rounded-full"
                  style={{ background: i <= clickCount ? '#FFB703' : '#ffffff50' }}
                />
              ))}
            </div>
          )}
        </motion.button>

        {/* Right side: Admin links, Universe toggle, user info, logout */}
        <div className="flex items-center space-x-4">
          {/* Admin/Educator quick links */}
          {showAdminLinks && (
            <div className="hidden md:flex items-center space-x-2">
              <button
                onClick={() => navigate('/analytics')}
                className="flex items-center gap-2 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-400 transition-colors text-sm font-bold"
                data-testid="analytics-link"
                title="Dashboard Analytique"
              >
                <FaChartLine />
                <span className="hidden lg:inline">Analytics</span>
              </button>
              <button
                onClick={() => navigate('/professor/articles')}
                className="flex items-center gap-2 px-3 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-400 transition-colors text-sm font-bold"
                data-testid="articles-link"
                title="Gestion des articles"
              >
                <FaNewspaper />
                <span className="hidden lg:inline">Articles</span>
              </button>
              <button
                onClick={() => navigate('/messaging')}
                className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-400 transition-colors text-sm font-bold"
                data-testid="messaging-link"
                title="Messagerie"
              >
                <FaEnvelope />
                <span className="hidden lg:inline">Messagerie</span>
              </button>
            </div>
          )}
          
          {/* Notification Center */}
          {user && <NotificationCenter />}
          
          <UniverseToggle />
          
          {user && (
            <div className="hidden md:block text-white text-sm">
              <span className="font-bold">{user.firstName || user.email?.split('@')[0]?.toUpperCase()}</span>
              <br />
              <span className="text-white/70">{user.schoolYear || 'Étudiant'}</span>
            </div>
          )}
          
          <button
            onClick={logout}
            className="comic-button bg-red-500 text-white px-4 py-2"
            data-testid="logout-button"
            title="Déconnexion"
          >
            <FaSignOutAlt className="text-xl" />
          </button>
        </div>
      </div>
    </header>
  );
}

export default AppHeader;
